// One-time migration importer UI.
//
// Flow: pick the `Apollo Towing Damage Fund.xlsx` → Preview (dry run,
// writes nothing) → review the reconciliation table → Confirm Import
// (commit). Import is refused unless every active sheet reconciles, and
// already-imported drivers are skipped, so this is safe to re-run.
import { useState } from 'react';
import { api } from '../api';
import { formatMoneySigned } from '../utils/money';
import ModalRoot from '../components/ModalRoot';

export default function DamageFundImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // dry-run result
  const [committed, setCommitted] = useState(null); // commit result
  const [force, setForce] = useState(false);       // wipe & re-import
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const fd = () => { const f = new FormData(); f.append('file', file); return f; };

  async function handlePreview() {
    setErr(''); setCommitted(null); setBusy(true);
    try { setPreview(await api.importDamageFund(fd(), { commit: false })); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }

  async function handleCommit() {
    setErr(''); setBusy(true);
    try {
      const res = await api.importDamageFund(fd(), { commit: true, force });
      setCommitted(res);
      onImported && onImported();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const rows = (committed || preview)?.rows || [];
  const totals = (committed || preview)?.totals;
  const allReconciled = preview && totals && totals.fail === 0;

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content df-modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h2 style={{ margin: 0 }}>Import from Spreadsheet</h2>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
          Upload <strong>Apollo Towing Damage Fund.xlsx</strong>. We'll reconcile every driver's
          balance first and show you the table. Nothing is saved until you click Confirm.
        </p>

        {!committed && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={e => { setFile(e.target.files[0] || null); setPreview(null); setErr(''); }}
            />
            <button type="button" className="btn btn-primary" disabled={!file || busy} onClick={handlePreview}>
              {busy && !preview ? 'Reading…' : 'Preview'}
            </button>
          </div>
        )}

        {err && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>
            {err}
          </div>
        )}

        {committed && (
          <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '12px 16px', color: '#065f46', fontSize: 14, marginBottom: 12 }}>
            <strong>Import complete.</strong> Imported {totals.ok} · skipped {totals.skip} (already imported) · failed {totals.fail}.
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Accounts were created unmapped — map each driver to their login from the company view so they can see their own balance.
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="table-wrapper" style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th style={{ textAlign: 'right' }}>Stated</th>
                  <th style={{ textAlign: 'right' }}>Computed</th>
                  <th style={{ textAlign: 'right' }}>Diff</th>
                  <th style={{ textAlign: 'right' }}>Txns</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.name}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoneySigned(r.stated)}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoneySigned(r.computed)}</td>
                    <td style={{ textAlign: 'right', color: r.reconciled ? '#64748b' : '#ef4444', fontWeight: r.reconciled ? 400 : 700 }}>
                      {formatMoneySigned(r.diff)}
                    </td>
                    <td style={{ textAlign: 'right', color: '#64748b' }}>{r.txns}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.reconciled
                        ? <span style={{ color: '#10b981', fontWeight: 600 }}>✓ {r.action}</span>
                        : <span style={{ color: '#ef4444', fontWeight: 700 }}>✗ {r.action}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {preview && !committed && (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer', marginBottom: 12 }}>
              <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} style={{ marginTop: 2 }} />
              <span>
                <strong>Spreadsheet changed — wipe &amp; re-import.</strong> Replaces each driver's
                imported history with the current file. Manually-entered transactions (charges,
                cash-outs, adjustments you added in the app) are kept.
              </span>
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: allReconciled ? '#065f46' : '#b91c1c', fontWeight: 600 }}>
                {allReconciled
                  ? (force ? `All ${totals.ok} drivers reconcile. Will wipe & re-import.` : `All ${totals.ok} drivers reconcile. Ready to import.`)
                  : `${totals.fail} driver(s) don't reconcile — fix the spreadsheet before importing. Nothing will be written.`}
              </div>
              <button type="button" className="btn btn-gold" disabled={!allReconciled || busy} onClick={handleCommit}>
                {busy ? 'Importing…' : force ? 'Wipe & Re-import' : 'Confirm Import'}
              </button>
            </div>
          </div>
        )}

        {committed && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </ModalRoot>
  );
}
